import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import AdminPage from '../../components/admin/AdminPage';
import {
  createOrganizationDoc,
  deleteOrganizationDoc,
  getOrganizationDocs,
  getOrganizationDocFileUrl,
  updateOrganizationDoc,
} from '../../services/userService';
import { getTraineeGroups } from '../../services/adminService';
import Loader from '../../components/common/Loader';

function OrganizationDocs() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // View state
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [previewDoc, setPreviewDoc] = useState(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [traineeGroups, setTraineeGroups] = useState([]);
  const [traineeGroupSearch, setTraineeGroupSearch] = useState('');
  const [selectedTraineeGroupIds, setSelectedTraineeGroupIds] = useState([]);
  const [visibilityMode, setVisibilityMode] = useState('all');
  const [groupPickerOpen, setGroupPickerOpen] = useState(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [sortBy, setSortBy] = useState('newest');

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [tagsInput, setTagsInput] = useState('');

  const fileInputRef = useRef(null);

  const [editId, setEditId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editTagsInput, setEditTagsInput] = useState('');

  const parseTags = (raw) => {
    const str = String(raw || '').trim();
    if (!str) return [];
    return str
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const fetchDocs = async (opts = {}) => {
    setLoading(true);
    try {
      const nextPage = opts.page !== undefined ? opts.page : page;
      const nextLimit = opts.limit !== undefined ? opts.limit : limit;
      const nextSearch = opts.search !== undefined ? opts.search : debouncedSearch;
      const nextTag = opts.tag !== undefined ? opts.tag : '';

      const params = {
        page: nextPage,
        limit: nextLimit,
      };
      if (nextSearch) params.search = nextSearch;
      if (nextTag) params.tag = nextTag;

      const resp = await getOrganizationDocs(params);
      const list = resp?.data?.docs || resp?.data?.data?.docs || resp?.docs || [];
      setDocs(Array.isArray(list) ? list : []);

      const pg = resp?.data?.pagination || resp?.data?.data?.pagination;
      if (pg) {
        setPage(pg.page || nextPage);
        setLimit(pg.limit || nextLimit);
        setTotalPages(pg.totalPages || 1);
        setTotalCount(pg.totalCount || 0);
      } else {
        setPage(nextPage);
        setLimit(nextLimit);
        setTotalPages(1);
        setTotalCount(Array.isArray(list) ? list.length : 0);
      }
    } catch (e) {
      toast.error(e?.response?.data?.data?.error_msg || 'Failed to load organization docs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDocs({ page: 1, search: debouncedSearch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await getTraineeGroups();
        if (res?.success && Array.isArray(res?.data?.groups)) setTraineeGroups(res.data.groups);
        else if (res?.success && Array.isArray(res?.data)) setTraineeGroups(res.data);
        else setTraineeGroups([]);
      } catch {
        setTraineeGroups([]);
      }
    };
    fetchGroups();
  }, []);

  const filteredDocs = useMemo(() => {
    const sorted = [...docs];
    if (sortBy === 'oldest') {
      sorted.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    } else if (sortBy === 'title_asc') {
      sorted.sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
    } else {
      sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }
    return sorted;
  }, [docs, sortBy]);

  const resetCreateForm = () => {
    setTitle('');
    setFile(null);
    setTagsInput('');
    setTraineeGroupSearch('');
    setSelectedTraineeGroupIds([]);
    setVisibilityMode('all');
    setGroupPickerOpen(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!file) {
      toast.error('File is required');
      return;
    }

    const formData = new FormData();
    formData.append('title', title.trim());
    formData.append('tags', JSON.stringify(parseTags(tagsInput)));
    formData.append('file', file);
    formData.append(
      'trainee_group_ids',
      JSON.stringify(visibilityMode === 'groups' ? selectedTraineeGroupIds || [] : [])
    );

    setSaving(true);
    try {
      const resp = await createOrganizationDoc(formData);
      if (resp?.success === false) {
        toast.error(resp?.data?.error_msg || 'Failed to create doc');
        return;
      }
      toast.success(resp?.data?.message || 'Doc added');
      resetCreateForm();
      setShowCreate(false);
      fetchDocs();
    } catch (e2) {
      toast.error(e2?.response?.data?.data?.error_msg || 'Failed to create doc');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (d) => {
    setEditId(d.id);
    setEditTitle(d.title || '');
    setEditTagsInput(Array.isArray(d.tags) ? d.tags.join(', ') : '');
    setShowEdit(true);
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditTitle('');
    setEditTagsInput('');
    setShowEdit(false);
  };

  const saveEdit = async (e) => {
    if (e) e.preventDefault();
    if (!editTitle.trim()) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: editTitle.trim(),
        tags: JSON.stringify(parseTags(editTagsInput)),
      };
      const resp = await updateOrganizationDoc(editId, payload);
      if (resp?.success === false) {
        toast.error(resp?.data?.error_msg || 'Failed to update doc');
        return;
      }
      toast.success(resp?.data?.message || 'Updated');
      cancelEdit();
      fetchDocs();
    } catch (e) {
      toast.error(e?.response?.data?.data?.error_msg || 'Failed to update doc');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (d) => {
    const ok = window.confirm('Delete this doc?');
    if (!ok) return;

    setSaving(true);
    try {
      const resp = await deleteOrganizationDoc(d.id);
      if (resp?.success === false) {
        toast.error(resp?.data?.error_msg || 'Failed to delete doc');
        return;
      }
      toast.success(resp?.data?.message || 'Deleted');
      fetchDocs();
    } catch (e) {
      toast.error(e?.response?.data?.data?.error_msg || 'Failed to delete doc');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  };

  const getFileIcon = (title = '') => {
    const ext = title.split('.').pop().toLowerCase();
    switch (ext) {
      case 'pdf': return { icon: 'fluent:document-pdf-24-filled', color: 'text-red-500' };
      case 'doc':
      case 'docx': return { icon: 'fluent:document-word-24-filled', color: 'text-blue-500' };
      case 'xls':
      case 'xlsx': return { icon: 'fluent:document-excel-24-filled', color: 'text-emerald-500' };
      case 'ppt':
      case 'pptx': return { icon: 'fluent:document-presentation-24-filled', color: 'text-orange-500' };
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return { icon: 'fluent:image-24-filled', color: 'text-purple-500' };
      default: return { icon: 'fluent:document-24-filled', color: 'text-gray-400' };
    }
  };

  return (
    <AdminPage noPadding>
      <div className="w-full max-w-[1440px] mx-auto px-6 pt-0 pb-12 animate-in fade-in duration-700">
        
        {/* Modern Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 pb-8 border-b border-gray-100 gap-6">
            <div className="space-y-1">
                <div className="flex items-center gap-2 text-[#7083FF] font-bold text-xs uppercase tracking-[0.2em] mb-2">
                    <Icon icon="fluent:folder-shield-24-regular" width={16} />
                    <span>Resource Center</span>
                </div>
                <h1 className="text-4xl font-black text-gray-900 tracking-tight">Organization Documents</h1>
                <p className="text-gray-500 font-medium max-w-lg">
                    Central repository for shared organization assets, templates, and guidelines. Manage document visibility and access.
                </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Global Refresh Action */}
              <button
                type="button"
                onClick={() => fetchDocs({ page: 1 })}
                className="p-3 rounded-2xl bg-white border border-gray-100 text-gray-400 hover:text-[#7083FF] hover:bg-[#7083FF]/5 transition-all shadow-sm group/refresh"
                title="Refresh Documents"
              >
                <Icon icon="fluent:arrow-sync-24-regular" width={20} className={`${loading ? 'animate-spin text-[#7083FF]' : 'group-active/refresh:rotate-180'} transition-transform duration-500`} />
              </button>

              <button
                type="button"
                onClick={() => setShowCreate(true)}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-primary text-white text-sm font-bold shadow-xl shadow-primary/20 hover:bg-primary/90 transition-all hover:scale-[1.02] active:scale-[0.98] w-full sm:w-auto"
              >
                <Icon icon="fluent:add-24-regular" width="20" height="20" />
                Add Document
              </button>
            </div>
        </div>

      {/* Filters Area */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-8">
        <div className="relative flex-1 group/search">
          <Icon icon="fluent:search-20-regular" className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within/search:text-primary transition-colors" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documents by title or tags..."
            className="w-full pl-11 pr-4 py-3.5 rounded-2xl border border-gray-100 bg-white text-sm shadow-sm focus:outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/30 transition-all"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* View Switcher */}
          <div className="flex p-1 bg-white rounded-2xl shadow-sm border border-gray-100 min-w-fit">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2.5 rounded-xl transition-all ${viewMode === 'grid' ? 'bg-primary/10 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
              title="Grid View"
            >
              <Icon icon="fluent:grid-20-regular" width="20" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2.5 rounded-xl transition-all ${viewMode === 'table' ? 'bg-primary/10 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
              title="Table View"
            >
              <Icon icon="fluent:list-20-regular" width="20" />
            </button>
          </div>

          <div className="flex-1 sm:flex-none flex items-center gap-2 bg-white p-1 rounded-2xl shadow-sm border border-gray-100 min-w-[160px]">
            <Icon icon="fluent:filter-20-regular" className="ml-3 w-4 h-4 text-gray-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full pl-2 pr-8 py-2.5 rounded-xl bg-transparent text-sm font-semibold focus:outline-none cursor-pointer"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="title_asc">Title A-Z</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 bg-white rounded-3xl border border-gray-100 shadow-sm text-center">
            <Loader message="Fetching documents..." />
        </div>
      ) : filteredDocs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 bg-white rounded-3xl border border-gray-100 shadow-sm">
          <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
            <Icon icon="fluent:document-question-mark-24-regular" className="text-gray-300 w-10 h-10" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">No documents found</h3>
          <p className="text-sm text-gray-500 mt-1">Try adjusting your search or filters</p>
          <button 
            onClick={() => { setSearch(''); setSortBy('newest'); }}
            className="mt-6 text-sm font-medium text-primary hover:underline"
          >
            Clear all filters
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredDocs.map((d) => {
            const { icon, color } = getFileIcon(d.title);
            return (
              <div 
                key={d.id} 
                className="group relative bg-white rounded-3xl border border-gray-100 p-5 shadow-sm hover:shadow-xl hover:shadow-gray-200/40 hover:-translate-y-1 transition-all duration-300"
              >
                {/* Header: Icon + Visibility */}
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-2xl bg-gray-50 group-hover:bg-white transition-colors`}>
                    <Icon icon={icon} className={`${color} w-8 h-8`} />
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {d.trainee_group_ids && d.trainee_group_ids.length > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-[10px] font-bold text-amber-600 uppercase tracking-tight">
                        <Icon icon="fluent:people-community-16-filled" />
                        Limited Access
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-[10px] font-bold text-emerald-600 uppercase tracking-tight">
                        <Icon icon="fluent:globe-16-filled" />
                        All Users
                      </span>
                    )}
                    <span className="text-[10px] text-gray-400 font-medium">{formatDate(d.createdAt)}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="mb-4">
                  <h3 className="text-sm font-bold text-gray-900 line-clamp-2 leading-snug group-hover:text-primary transition-colors h-10" title={d.title}>
                    {d.title}
                  </h3>
                  {Array.isArray(d.tags) && d.tags.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1.5 h-6 overflow-hidden">
                      {d.tags.slice(0, 2).map((t) => (
                        <span key={t} className="text-[10px] px-2 py-0.5 rounded-lg bg-gray-50 text-gray-500 font-medium">#{t}</span>
                      ))}
                      {d.tags.length > 2 && <span className="text-[10px] text-gray-400">+{d.tags.length - 2}</span>}
                    </div>
                  ) : <div className="mt-2 h-6" />}
                </div>

                {/* Footer: Actions */}
                <div className="flex items-center gap-2 pt-4 border-t border-gray-50">
                  <button
                    onClick={() => setPreviewDoc(d)}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-primary/5 text-primary text-xs font-bold hover:bg-primary hover:text-white transition-all underline-none"
                  >
                    <Icon icon="fluent:eye-20-regular" />
                    Preview
                  </button>
                  <button
                    type="button"
                    onClick={() => startEdit(d)}
                    className="p-2 rounded-xl border border-gray-100 text-gray-400 hover:text-primary hover:border-primary/30 transition-all"
                    title="Edit Details"
                  >
                    <Icon icon="fluent:edit-20-regular" width="18" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(d)}
                    className="p-2 rounded-xl border border-gray-100 text-gray-400 hover:text-red-500 hover:border-red-100 hover:bg-red-50 transition-all"
                    title="Delete Document"
                  >
                    <Icon icon="fluent:delete-20-regular" width="18" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden animate-in slide-in-from-bottom-2 duration-300">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50/50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Document</th>
                  <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Visibility</th>
                  <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Created At</th>
                  <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredDocs.map((d) => {
                  const { icon, color } = getFileIcon(d.title);
                  return (
                    <tr key={d.id} className="hover:bg-gray-50/20 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <div className={`p-2.5 rounded-xl bg-gray-50 shrink-0`}>
                            <Icon icon={icon} className={`${color} w-6 h-6`} />
                          </div>
                          <div>
                            <div className="text-sm font-bold text-gray-900">{d.title}</div>
                            {Array.isArray(d.tags) && d.tags.length > 0 && (
                              <div className="mt-0.5 flex flex-wrap gap-1.5">
                                {d.tags.slice(0, 3).map((t) => (
                                  <span key={t} className="text-[10px] text-gray-400">#{t}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {d.trainee_group_ids && d.trainee_group_ids.length > 0 ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-[10px] font-bold text-amber-600 border border-amber-100/50">
                            <Icon icon="fluent:people-community-16-filled" />
                            {d.trainee_group_ids.length} Groups
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-[10px] font-bold text-emerald-600 border border-emerald-100/50">
                            <Icon icon="fluent:globe-16-filled" />
                            Everyone
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 border-none">
                        <span className="text-xs text-gray-500 font-medium">{formatDate(d.createdAt)}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setPreviewDoc(d)}
                            className="p-2 rounded-xl text-primary hover:bg-primary/5 transition-all"
                            title="Preview"
                          >
                            <Icon icon="fluent:eye-20-regular" width="18" />
                          </button>
                          <button
                            onClick={() => startEdit(d)}
                            className="p-2 rounded-xl text-gray-400 hover:text-primary hover:bg-primary/5 transition-all"
                            title="Edit"
                          >
                            <Icon icon="fluent:edit-20-regular" width="18" />
                          </button>
                          <button
                            onClick={() => handleDelete(d)}
                            className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                            title="Delete"
                          >
                            <Icon icon="fluent:delete-20-regular" width="18" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination Hub */}
      {totalCount > 0 && (
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-gray-50/50 rounded-[24px] border border-gray-100">
          <p className="text-sm font-medium text-gray-500">
            Showing <span className="text-gray-900 font-bold">{docs.length}</span> of <span className="text-gray-900 font-bold">{totalCount}</span> documents
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchDocs({ page: page - 1 })}
                disabled={page === 1}
                className="p-2 rounded-xl bg-white border border-gray-100 text-gray-500 hover:text-[#7083FF] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
              >
                <Icon icon="fluent:chevron-left-24-regular" width="20" />
              </button>
              <div className="flex items-center px-4 py-2 bg-white rounded-xl border border-gray-100 shadow-sm">
                <span className="text-sm font-bold text-[#7083FF]">{page}</span>
                <span className="text-sm font-medium text-gray-300 mx-1">/</span>
                <span className="text-sm font-medium text-gray-500">{totalPages}</span>
              </div>
              <button
                onClick={() => fetchDocs({ page: page + 1 })}
                disabled={page === totalPages}
                className="p-2 rounded-xl bg-white border border-gray-100 text-gray-500 hover:text-[#7083FF] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
              >
                <Icon icon="fluent:chevron-right-24-regular" width="20" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {previewDoc && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-gray-900/40 backdrop-blur-xl animate-in fade-in duration-300">
          <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-100">
            <div className="flex items-center gap-4">
              <Icon icon={getFileIcon(previewDoc.title).icon} className={`${getFileIcon(previewDoc.title).color} w-8 h-8`} />
              <h2 className="text-lg font-bold text-gray-900">{previewDoc.title}</h2>
            </div>
            <div className="flex items-center gap-3">
              <a href={getOrganizationDocFileUrl(previewDoc.id)} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-xl bg-primary text-white text-xs font-bold hover:bg-primary/90 transition-all">
                Open in new tab
              </a>
              <button onClick={() => setPreviewDoc(null)} className="p-2 rounded-full hover:bg-gray-100 text-gray-400">
                <Icon icon="fluent:dismiss-24-regular" width="24" />
              </button>
            </div>
          </div>
          <div className="flex-1 bg-gray-100 p-8 flex items-center justify-center">
            <iframe src={getOrganizationDocFileUrl(previewDoc.id)} className="w-full h-full max-w-5xl bg-white shadow-2xl rounded-2xl" />
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-xl bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Edit Document</h2>
              <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600"><Icon icon="fluent:dismiss-24-regular" width={24} /></button>
            </div>
            <form onSubmit={saveEdit} className="p-8 space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700">Document Title</label>
                <input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-100 focus:ring-2 focus:ring-primary/10 outline-none transition-all"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700">Tags (comma separated)</label>
                <input
                  value={editTagsInput}
                  onChange={(e) => setEditTagsInput(e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-100 focus:ring-2 focus:ring-primary/10 outline-none transition-all"
                  placeholder="e.g. policy, onboarding"
                />
              </div>
              <div className="pt-4 border-t border-gray-100 flex justify-end gap-3">
                <button type="button" onClick={cancelEdit} className="px-6 py-2.5 rounded-xl border border-gray-200 font-bold text-gray-500">Cancel</button>
                <button type="submit" disabled={saving} className="px-8 py-2.5 rounded-xl bg-primary text-white font-bold shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all">
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-xl bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Add Document</h2>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600"><Icon icon="fluent:dismiss-24-regular" width={24} /></button>
            </div>
            <form onSubmit={handleCreate} className="p-8 space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700">Document Title</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} border className="w-full px-4 py-3 rounded-2xl border border-gray-100 focus:ring-2 focus:ring-primary/10 outline-none transition-all" required />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700">Tags (comma separated)</label>
                <input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} className="w-full px-4 py-3 rounded-2xl border border-gray-100 focus:ring-2 focus:ring-primary/10 outline-none transition-all" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700">File</label>
                <input type="file" ref={fileInputRef} onChange={(e) => setFile(e.target.files?.[0])} className="hidden" />
                <div onClick={() => fileInputRef.current.click()} className="p-10 border-2 border-dashed border-gray-100 rounded-3xl flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-all">
                   <Icon icon="fluent:arrow-upload-24-regular" width={40} className="text-gray-300 mb-2" />
                   <p className="text-sm font-bold text-gray-400">{file ? file.name : 'Choose file...'}</p>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-100 flex justify-end gap-3">
                <button type="button" onClick={() => setShowCreate(false)} className="px-6 py-2.5 rounded-xl border border-gray-200 font-bold text-gray-500">Cancel</button>
                <button type="submit" disabled={saving} className="px-8 py-2.5 rounded-xl bg-primary text-white font-bold shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all">
                  {saving ? 'Creating...' : 'Create Document'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .admin-page { padding: 0 !important; }
      `}</style>
      </div>
    </AdminPage>
  );
}

export default OrganizationDocs;
